import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "wouter";
import {
  useGetLmsExam,
  useGetLmsProgress,
  useUpdateLmsProgress,
  useSubmitLmsAssessment,
  getGetLmsProgressQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  GraduationCap,
  Loader2,
  Receipt,
  RotateCcw,
  ShieldCheck,
  TrendingUp,
  Wifi,
  XCircle,
} from "lucide-react";

// The five training slides. Their content teaches everything the product exam
// checks, so an attentive trainee can pass. Keep this in sync with TOTAL_SLIDES
// on the server (artifacts/api-server/src/routes/lms.ts).
const SLIDES: {
  title: string;
  icon: typeof GraduationCap;
  body: ReactNode;
}[] = [
  {
    title: "Welcome to Mooya",
    icon: GraduationCap,
    body: (
      <>
        <p>
          Welcome to Mooya agent training. As a Mooya agent you sell{" "}
          <strong>fibre internet connections</strong> to households and earn
          commission for every line you bring on board.
        </p>
        <p>
          This short module has five slides followed by a 10-question exam. You
          need <strong>8 out of 10</strong> correct to pass. You can review the
          material and retry as many times as you need.
        </p>
        <p>
          Everything in the portal — your earnings, balances and payouts — is
          shown in <strong>South African Rand (ZAR)</strong>.
        </p>
      </>
    ),
  },
  {
    title: "Openserve & FTTH",
    icon: Wifi,
    body: (
      <>
        <p>
          Mooya sells fibre products on the <strong>Openserve</strong> network.
          Openserve builds and maintains the fibre infrastructure; we connect
          customers to it.
        </p>
        <p>
          <strong>FTTH</strong> stands for <strong>Fibre to the Home</strong> —
          a dedicated fibre line run all the way to the customer&apos;s house
          for fast, reliable internet.
        </p>
        <p>
          Once a customer signs up, Openserve schedules an installation. The
          line becomes active once it is installed and activated.
        </p>
      </>
    ),
  },
  {
    title: "Once-off fees",
    icon: Receipt,
    body: (
      <>
        <p>
          A <strong>once-off connection fee</strong> is a one-time
          installation/activation charge the customer pays when they sign up. It
          is charged a single time, not every month.
        </p>
        <p>
          This is separate from the customer&apos;s monthly subscription, which
          they pay for as long as they keep the service.
        </p>
        <p>
          Always be clear with customers about what is once-off versus what is
          recurring so there are no surprises on their first bill.
        </p>
      </>
    ),
  },
  {
    title: "Tiered recurring commission",
    icon: TrendingUp,
    body: (
      <>
        <p>
          <strong>Recurring commission</strong> is a monthly commission you earn
          for as long as the customer&apos;s line stays active. The longer your
          customers stay, the more you earn.
        </p>
        <p>
          Commission is <strong>tiered</strong>: the rate increases as your
          active customer base grows, so building a larger book of active
          customers pays off over time.
        </p>
        <p>
          You become eligible to earn on an order{" "}
          <strong>once it is installed and activated</strong> — not when the
          quote is given. If a customer cancels within the contracted period,
          commission for that line can be reversed (clawed back).
        </p>
      </>
    ),
  },
  {
    title: "Compliance & support",
    icon: ShieldCheck,
    body: (
      <>
        <p>
          To onboard you must complete <strong>FICA</strong> verification. Your
          banking details are verified with a{" "}
          <strong>bank confirmation letter</strong> so commission reaches the
          right account.
        </p>
        <p>
          If a customer reports a fault after installation, don&apos;t promise
          fixes or refunds yourself — <strong>log a support ticket</strong> so
          it is routed to the support team who can resolve it.
        </p>
        <p>
          Act honestly, keep your customers informed, and use the portal to
          track every order and earning.
        </p>
      </>
    ),
  },
];

const TOTAL_SLIDES = SLIDES.length;

type Phase = "slides" | "exam" | "result";

export function LmsTrainingModule() {
  const { data: exam, isLoading: examLoading } = useGetLmsExam();
  const { data: progress, isLoading: progressLoading } = useGetLmsProgress();
  const updateProgress = useUpdateLmsProgress();
  const submitAssessment = useSubmitLmsAssessment();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>("slides");
  const [slideIndex, setSlideIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // Restore the trainee's saved position once, after progress first loads. A
  // saved slide >= TOTAL_SLIDES means they had reached the exam.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !progress) return;
    restored.current = true;
    if (progress.currentSlide >= TOTAL_SLIDES) {
      setPhase("exam");
      setSlideIndex(TOTAL_SLIDES - 1);
    } else {
      setSlideIndex(
        Math.max(0, Math.min(TOTAL_SLIDES - 1, progress.currentSlide)),
      );
    }
  }, [progress]);

  // Persist the trainee's position so they can resume later. Best-effort: a
  // failed save must not block navigation.
  function persistSlide(index: number) {
    updateProgress.mutate(
      { data: { currentSlide: index } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetLmsProgressQueryKey(),
          });
        },
      },
    );
  }

  function goToSlide(index: number) {
    setSlideIndex(index);
    persistSlide(index);
  }

  function startExam() {
    setPhase("exam");
    // Record that the slides are done and the trainee is on the exam.
    persistSlide(TOTAL_SLIDES);
  }

  const answeredCount = exam
    ? exam.questions.filter((q) => answers[q.id]).length
    : 0;
  const allAnswered = exam ? answeredCount === exam.questions.length : false;

  async function handleSubmit() {
    if (!exam || !allAnswered) return;
    await submitAssessment.mutateAsync({
      data: {
        answers: exam.questions.map((q) => ({
          questionId: q.id,
          selectedKey: answers[q.id],
        })),
      },
    });
    queryClient.invalidateQueries({ queryKey: getGetLmsProgressQueryKey() });
    setPhase("result");
  }

  function reviewAndRetry() {
    setAnswers({});
    submitAssessment.reset();
    setSlideIndex(0);
    setPhase("slides");
    persistSlide(0);
  }

  if (examLoading || progressLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const result = submitAssessment.data ?? null;
  const alreadyPassed = progress?.completed ?? false;

  return (
    <div className="font-sans min-h-[100dvh] w-full max-w-[480px] mx-auto bg-gray-50 flex flex-col text-[#0B132B]">
      <header className="bg-[#0B132B] text-white px-5 pt-6 pb-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <img
              src={`${import.meta.env.BASE_URL}mooya-mark.png`}
              alt="Mooya"
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-lg tracking-tight">Mooya</span>
          </div>
          <Link
            href="/home"
            className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Exit
          </Link>
        </div>
        <h1 className="text-xl font-bold mb-1">Agent training</h1>
        <p className="text-sm text-slate-300">
          {phase === "exam"
            ? "Product knowledge exam"
            : phase === "result"
              ? "Your result"
              : `Slide ${slideIndex + 1} of ${TOTAL_SLIDES}`}
        </p>
        {phase !== "result" ? (
          <div className="mt-4 h-1.5 w-full rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#E6007E] transition-all"
              style={{
                width:
                  phase === "exam"
                    ? "100%"
                    : `${((slideIndex + 1) / TOTAL_SLIDES) * 100}%`,
              }}
            />
          </div>
        ) : null}
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-6">
        {alreadyPassed && phase !== "result" ? (
          <div className="mb-5 flex items-start gap-3 rounded-xl bg-[#00A859]/10 p-4">
            <BadgeCheck className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
            <p className="text-sm text-[#0B132B]">
              You&apos;ve already completed your training. You can review the
              material or retake the exam any time.
            </p>
          </div>
        ) : null}

        {phase === "slides" ? (
          <SlideView index={slideIndex} />
        ) : phase === "exam" && exam ? (
          <ExamView
            questions={exam.questions}
            answers={answers}
            onSelect={(qid, key) =>
              setAnswers((prev) => ({ ...prev, [qid]: key }))
            }
          />
        ) : phase === "result" && result ? (
          <ResultView result={result} onRetry={reviewAndRetry} />
        ) : null}
      </main>

      {phase === "slides" ? (
        <footer className="px-5 py-4 border-t border-gray-100 bg-white flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => goToSlide(slideIndex - 1)}
            disabled={slideIndex === 0}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0B132B] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {slideIndex < TOTAL_SLIDES - 1 ? (
            <button
              type="button"
              onClick={() => goToSlide(slideIndex + 1)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#E6007E] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#c4006b] transition-colors"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={startExam}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#E6007E] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#c4006b] transition-colors"
            >
              Start exam <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </footer>
      ) : phase === "exam" ? (
        <footer className="px-5 py-4 border-t border-gray-100 bg-white">
          {submitAssessment.isError ? (
            <p className="mb-2 text-sm text-red-700">
              Something went wrong submitting your exam. Please try again.
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-500">
              Answered {answeredCount} of {exam?.questions.length ?? 0}
            </span>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allAnswered || submitAssessment.isPending}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#E6007E] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c4006b] transition-colors"
            >
              {submitAssessment.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              Submit exam
            </button>
          </div>
        </footer>
      ) : null}
    </div>
  );
}

function SlideView({ index }: { index: number }) {
  const slide = SLIDES[index];
  const Icon = slide.icon;
  return (
    <article className="rounded-2xl border border-gray-100 bg-white p-6">
      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#E6007E]/10 mb-4">
        <Icon className="w-6 h-6 text-[#E6007E]" />
      </div>
      <h2 className="text-lg font-bold text-[#0B132B] mb-3">{slide.title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-gray-700 [&_strong]:text-[#0B132B] [&_strong]:font-semibold">
        {slide.body}
      </div>
    </article>
  );
}

function ExamView({
  questions,
  answers,
  onSelect,
}: {
  questions: {
    id: string;
    prompt: string;
    options: { key: string; label: string }[];
  }[];
  answers: Record<string, string>;
  onSelect: (questionId: string, key: string) => void;
}) {
  return (
    <div className="space-y-4">
      {questions.map((q, i) => (
        <fieldset
          key={q.id}
          className="rounded-2xl border border-gray-100 bg-white p-4"
        >
          <legend className="sr-only">{q.prompt}</legend>
          <p className="text-sm font-semibold text-[#0B132B] mb-3">
            {i + 1}. {q.prompt}
          </p>
          <div className="space-y-2">
            {q.options.map((opt) => {
              const selected = answers[q.id] === opt.key;
              return (
                <label
                  key={opt.key}
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                    selected
                      ? "border-[#E6007E] bg-[#E6007E]/5"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name={q.id}
                    value={opt.key}
                    checked={selected}
                    onChange={() => onSelect(q.id, opt.key)}
                    className="mt-0.5 h-4 w-4 accent-[#E6007E]"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

function ResultView({
  result,
  onRetry,
}: {
  result: {
    score: number;
    passed: boolean;
    totalQuestions: number;
    passMark: number;
  };
  onRetry: () => void;
}) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 text-center">
      <div
        className={`mx-auto flex items-center justify-center w-14 h-14 rounded-full mb-4 ${
          result.passed ? "bg-[#00A859]/10" : "bg-red-50"
        }`}
      >
        {result.passed ? (
          <CheckCircle2 className="w-7 h-7 text-[#00A859]" />
        ) : (
          <XCircle className="w-7 h-7 text-red-600" />
        )}
      </div>
      <h2 className="text-lg font-bold text-[#0B132B] mb-1">
        {result.passed ? "You passed!" : "Not quite there"}
      </h2>
      <p className="text-sm text-gray-600 mb-1">
        You scored{" "}
        <strong className="text-[#0B132B]">
          {result.score} / {result.totalQuestions}
        </strong>
        .
      </p>
      <p className="text-sm text-gray-500 mb-6">
        {result.passed
          ? "Your training is complete."
          : `You need ${result.passMark} correct to pass. Review the material and try again.`}
      </p>
      {result.passed ? (
        <Link
          href="/home"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E6007E] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#c4006b] transition-colors"
        >
          Continue
        </Link>
      ) : (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E6007E] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#c4006b] transition-colors"
        >
          <RotateCcw className="w-4 h-4" /> Review material &amp; retry
        </button>
      )}
    </div>
  );
}
